import { Controller } from "@hotwired/stimulus"
import { renderStreamMessage } from "@hotwired/turbo"

// Drag-to-reschedule / drag-to-reassign on the Planner grid (TASK-238.3,
// decision-65 §5, doc-39 §3). Plain HTML5 drag/drop — there is no other way
// to do this headlessly either (agent-conventions.md), so the system spec
// dispatches the same dragstart/drop events a mouse would.
//
// A card knows its OWN reschedule/reassign PATCH urls (rendered server-side,
// per visit) and never moves in the DOM on drop: the server's refresh_frame
// re-renders the whole grid once a change actually lands, so there is
// nothing to reconcile if it doesn't (a scope dialog, a failed save) — the
// card just sits back where the server last put it.
//
// Root element is the grid; cards and cells are marked with plain data
// attributes rather than Stimulus targets, because both are rebuilt by a
// server-rendered frame on every refresh and the controller itself never
// needs to enumerate them, only read whichever one dispatched the event.
export default class extends Controller {
  dragstart(event) {
    const card = event.target.closest("[data-visit-id]")
    if (!card) return

    this.draggedCard = card
    event.dataTransfer.effectAllowed = "move"
    // Firefox refuses to start a drag at all without at least one
    // setData call; the actual payload the drop handler uses is the JS
    // reference above, kept in the same page/controller instance.
    event.dataTransfer.setData("text/plain", card.dataset.visitId)
    card.classList.add("opacity-50")
  }

  dragend(event) {
    event.target.closest("[data-visit-id]")?.classList.remove("opacity-50")
    this.draggedCard = null
  }

  dragover(event) {
    event.preventDefault()
    event.currentTarget.classList.add("ring-2", "ring-blue-500")
  }

  dragleave(event) {
    event.currentTarget.classList.remove("ring-2", "ring-blue-500")
  }

  drop(event) {
    event.preventDefault()
    const targetCell = event.currentTarget
    targetCell.classList.remove("ring-2", "ring-blue-500")

    const card = this.draggedCard
    this.draggedCard = null
    if (!card) return

    const sourceCell = card.closest("[data-scheduled-on]")
    if (!sourceCell) return

    const sameDay = sourceCell.dataset.scheduledOn === targetCell.dataset.scheduledOn
    const sameAssignee = (sourceCell.dataset.assigneeMemberId || "") === (targetCell.dataset.assigneeMemberId || "")
    if (sameDay && sameAssignee) return // dropped back where it started

    this.move(card, {
      scheduledOn: sameDay ? null : targetCell.dataset.scheduledOn,
      assigneeMemberId: sameAssignee ? null : (targetCell.dataset.assigneeMemberId || "")
    })
  }

  // Sequential, never parallel: a diagonal drop (new day AND new row)
  // reschedules first, and only follows with the reassign if that first
  // PATCH actually applied. If it opened the series-scope dialog instead
  // (a live series member with no answer yet), chasing it immediately with
  // a reassign on the same, still-unmoved visit would pop the SAME question
  // a second time back to back — reads as broken, not thorough. The user
  // finishes the dialog, and the reassign the drop also asked for is simply
  // not applied; dragging the now-moved card again covers it.
  async move(card, { scheduledOn, assigneeMemberId }) {
    if (scheduledOn) {
      const applied = await this.patch(card.dataset.reschedulePath, { scheduled_on: scheduledOn })
      if (!applied) return
    }
    if (assigneeMemberId !== null) {
      await this.patch(card.dataset.reassignPath, { assignee_member_id: assigneeMemberId })
    }
  }

  // Fetch, render whatever stream comes back exactly as a Turbo form
  // submission would, and report whether the change actually applied (vs
  // the response being the `open_modal` scope dialog instead).
  async patch(url, body) {
    if (!url) return false

    const response = await fetch(url, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "Accept": "text/vnd.turbo-stream.html",
        "X-CSRF-Token": this.csrfToken
      },
      body: JSON.stringify({ ...body, return_to: window.location.href })
    })

    const html = await response.text()
    renderStreamMessage(html)
    return !html.includes('action="open_modal"')
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }
}
